/**
 * 大运计算引擎 v2 — 命局联动版
 * 核心原则：大运评分必须同时纳入原局喜忌、大运与原局的刑冲合害、格局助破
 * 输出结构：十年主题 / 触发点 / 风险位 / 主动发力方向
 */

import {
  STEMS, BRANCHES,
  type Stem, type Branch, type Element, type FourPillars,
  STEM_ELEMENT, BRANCH_HIDDEN,
} from '../../baziCalc';
import { getShishen, ELEMENT_COLORS, type Shishen } from './shishenEngine';
import { calcDayStrength } from './xiyongEngine';
import { judgeGeju, type GejuResult } from './gejuEngine';
import { getSolarTermDate, daysBetweenExact, calcQiyunAge, monthToTermIndex, toJulianDay } from './solarTermEngine';
import {
  ELEMENT_GENERATES,
  ELEMENT_OVERCOMES,
  ELEMENT_GENERATED_BY,
  ELEMENT_OVERCOME_BY,
} from '@/utils/calcTools/unifiedRules';

// ── 刑冲合害规则（与 liunianEngine 保持一致）─────────────────
const TIAN_GAN_HE: [Stem, Stem][] = [
  ['甲', '己'], ['乙', '庚'], ['丙', '辛'], ['丁', '壬'], ['戊', '癸'],
];
const TIAN_GAN_CHONG: [Stem, Stem][] = [
  ['甲', '庚'], ['乙', '辛'], ['丙', '壬'], ['丁', '癸'],
];
const DI_ZHI_CHONG: [Branch, Branch][] = [
  ['子', '午'], ['丑', '未'], ['寅', '申'], ['卯', '酉'], ['辰', '戌'], ['巳', '亥'],
];
const DI_ZHI_HAI: [Branch, Branch][] = [
  ['子', '未'], ['丑', '午'], ['寅', '巳'], ['卯', '辰'], ['申', '亥'], ['酉', '戌'],
];
const DI_ZHI_LIU_HE: [Branch, Branch][] = [
  ['子', '丑'], ['寅', '亥'], ['卯', '戌'], ['辰', '酉'], ['巳', '申'], ['午', '未'],
];
const DI_ZHI_SAN_HE: [Branch, Branch, Branch, Element][] = [
  ['申', '子', '辰', '水'],
  ['巳', '酉', '丑', '金'],
  ['寅', '午', '戌', '火'],
  ['亥', '卯', '未', '木'],
];
const DI_ZHI_BAN_HE: [Branch, Branch, Element][] = [
  ['申', '子', '水'], ['子', '辰', '水'],
  ['巳', '酉', '金'], ['酉', '丑', '金'],
  ['寅', '午', '火'], ['午', '戌', '火'],
  ['亥', '卯', '木'], ['卯', '未', '木'],
];
const DI_ZHI_SAN_XING: [Branch, Branch, Branch][] = [
  ['寅', '巳', '申'], ['丑', '戌', '未'],
];
const DI_ZHI_ZI_XING: Branch[] = ['辰', '午', '酉', '亥'];
const DI_ZHI_PO: [Branch, Branch][] = [
  ['子', '酉'], ['丑', '辰'], ['寅', '亥'], ['卯', '午'], ['巳', '申'], ['未', '戌'],
];

function hasPair(pairs: Array<[string, string]>, a: string, b: string): boolean {
  return pairs.some(([x, y]) => (x === a && y === b) || (x === b && y === a));
}

function isTianGanHe(a: Stem, b: Stem): boolean { return hasPair(TIAN_GAN_HE, a, b); }
function isTianGanChong(a: Stem, b: Stem): boolean { return hasPair(TIAN_GAN_CHONG, a, b); }
function isChong(a: Branch, b: Branch): boolean { return hasPair(DI_ZHI_CHONG, a, b); }
function isHe(a: Branch, b: Branch): boolean { return hasPair(DI_ZHI_LIU_HE, a, b); }
function isHai(a: Branch, b: Branch): boolean { return hasPair(DI_ZHI_HAI, a, b); }
function isPo(a: Branch, b: Branch): boolean { return hasPair(DI_ZHI_PO, a, b); }
function isZiXing(a: Branch, b: Branch): boolean { return a === b && DI_ZHI_ZI_XING.includes(a); }
function isSanHe(allBranches: Branch[]): { element: Element; branches: Branch[] } | null {
  for (const [x, y, z, el] of DI_ZHI_SAN_HE) {
    if (allBranches.includes(x) && allBranches.includes(y) && allBranches.includes(z)) {
      return { element: el, branches: [x, y, z] };
    }
  }
  return null;
}
function isBanHe(a: Branch, b: Branch): { element: Element } | null {
  for (const [x, y, el] of DI_ZHI_BAN_HE) {
    if ((x === a && y === b) || (x === b && y === a)) return { element: el };
  }
  return null;
}
function isSanXing(allBranches: Branch[]): boolean {
  return DI_ZHI_SAN_XING.some(([x, y, z]) => allBranches.includes(x) && allBranches.includes(y) && allBranches.includes(z));
}

// ── 辅助函数 ──────────────────────────────────────────────────
function getBranchMainEl(branch: Branch): Element {
  const hidden = BRANCH_HIDDEN[branch];
  return hidden.length > 0 ? STEM_ELEMENT[hidden[0].stem] : '土';
}
function getBranchMainStem(branch: Branch): Stem {
  const hidden = BRANCH_HIDDEN[branch];
  return hidden.length > 0 ? hidden[0].stem : '己';
}

// ── 数据类型 ──────────────────────────────────────────────────
export interface DayunConflictItem {
  type: '冲' | '合' | '刑' | '害' | '破' | '三合' | '半合' | '天干合' | '天干冲';
  target: string;
  effect: '吉' | '凶' | '中性';
  desc: string;
}

export interface DayunDomain {
  score: number;
  level: '优秀' | '良好' | '平稳' | '注意' | '谨慎';
  desc: string;
}

export interface DayunItem {
  index: number;
  stem: Stem;
  branch: Branch;
  startAge: number;
  startMonths: number;
  endAge: number;
  startYear: number;
  endYear: number;
  stemShishen: Shishen;
  branchMainShishen: Shishen;
  stemColor: string;
  branchColor: string;
  isActive: boolean;

  // NEW: 命局联动分析
  stemEl: Element;
  branchEl: Element;
  bodyStrength: 'strong' | 'weak' | 'neutral';
  xiElements: Element[];
  jiElements: Element[];
  conflicts: DayunConflictItem[];
  overallScore: number;
  overallLevel: '大吉' | '吉' | '平顺' | '需注意' | '挑战';

  // 十年主题结构
  decadeTheme: string;
  triggerPoint: string;
  riskArea: string;
  proactive: string;

  // 四域评分
  career: DayunDomain;
  wealth: DayunDomain;
  love: DayunDomain;
  health: DayunDomain;

  keyAdvice: string[];
  factors: string[];
}

export interface DayunResult {
  startAge: number;
  startMonths: number;
  isForward: boolean;
  /** 精确交运时刻，格式 YYYY-MM-DD HH:mm */
  startDateTime: string;
  items: DayunItem[];
  currentDayun?: DayunItem;
}

// ── 年干阴阳 ──────────────────────────────────────────────────
const STEM_YIN_YANG_MAP: Record<Stem, 'yang' | 'yin'> = {
  甲: 'yang', 丙: 'yang', 戊: 'yang', 庚: 'yang', 壬: 'yang',
  乙: 'yin',  丁: 'yin',  己: 'yin',  辛: 'yin',  癸: 'yin',
};

/**
 * 将天数差转换为公历日期（出生时刻 + N天）
 * 用于计算精确交运时刻
 */
function addDaysToDate(
  year: number, month: number, day: number, hour: number, minute: number,
  daysToAdd: number,
): { year: number; month: number; day: number; hour: number; minute: number } {
  // 将出生时刻转为小时数，加上天数对应的小时
  const totalHours = hour + minute / 60 + daysToAdd * 24;
  const baseJDN = toJulianDay(year, month, day, 0, 0, 0);
  const newJDN = baseJDN + totalHours / 24;
  const t = (newJDN - 2451545.0) / 36525;
  // 简单从 JDN 转回日期
  const z = Math.floor(newJDN + 0.5);
  const f = newJDN + 0.5 - z;
  const a = z < 2299161 ? z : z + 1 + Math.floor((z - 1867216.25) / 36524.25) - Math.floor(Math.floor((z - 1867216.25) / 36524.25) / 4);
  const b = a + 1524;
  const c = Math.floor((b - 122.1) / 365.25);
  const d = Math.floor(365.25 * c);
  const e = Math.floor((b - d) / 30.6001);
  const dayFrac = b - d - Math.floor(30.6001 * e) + f;
  const mo = e < 14 ? e - 1 : e - 13;
  const yr = mo > 2 ? c - 4716 : c - 4715;
  const dy = Math.floor(dayFrac);
  const fracHours = (dayFrac - dy) * 24;
  const hr = Math.floor(fracHours);
  const mn = Math.round((fracHours - hr) * 60);
  return { year: yr, month: mo, day: dy, hour: hr % 24, minute: mn >= 60 ? 59 : mn };
}

function calcStartAgeAndDirection(
  birthYear: number, birthMonth: number, birthDay: number,
  birthHour: number, birthMinute: number,
  yearStem: Stem, gender: 'male' | 'female',
): { startAge: number; startMonths: number; isForward: boolean; startDateTime: string } {
  const yearYY = STEM_YIN_YANG_MAP[yearStem];
  const isForward = (yearYY === 'yang' && gender === 'male') || (yearYY === 'yin' && gender === 'female');
  let days = 0;
  const currentTermIdx = monthToTermIndex(birthMonth);

  /**
   * 根据"原始索引"（可为负数或超过11）获取节气日期
   * rawIdx=0 → 立春(idx=0), rawIdx=-1 → 上一年小寒(idx=11), rawIdx=12 → 下一年立春...
   *
   * 关键：getSolarTermDate(year, 11) 内部返回的是 year+1 年的小寒（1月）
   * 所以要获取 targetYear 年的小寒，需要传入 targetYear-1
   */
  function getTermDateByRawIdx(rawIdx: number): ReturnType<typeof getSolarTermDate> {
    const termIdx = ((rawIdx % 12) + 12) % 12;
    // 计算该节气所属的命理年份
    // rawIdx 每变化12就跨一个命理年
    const yearShift = Math.floor(rawIdx / 12);
    let termYear = birthYear + yearShift;
    // 关键修正：getSolarTermDate(y, 11) 内部返回 y+1 年1月的小寒
    // 因此：
    //   - 若 termIdx=11（小寒），getSolarTermDate(termYear, 11) 返回 termYear+1 年的小寒
    //   - 若我们想要 targetCalYear 年的小寒，需要传入 targetCalYear-1
    //
    // 对于 rawIdx >= 0（顺排向前找）：
    //   rawIdx=11 时，termIdx=11，yearShift=0，termYear=birthYear
    //   getSolarTermDate(birthYear, 11) → birthYear+1 年的小寒
    //   但如果出生在1月（birthMonth=1），当月的节就是 birthYear 年的小寒
    //   所以需要 termYear-1，让函数返回 birthYear 年的小寒
    //
    // 判断条件：termIdx=11 且 rawIdx 在 [0,11] 范围内（即同一命理年内的小寒）
    // 且出生月是1月（birthMonth=1，说明出生在小寒所在的公历月份）
    if (termIdx === 11 && rawIdx >= 0 && rawIdx < 12 && birthMonth === 1) {
      termYear -= 1;
    }
    return getSolarTermDate(termYear, termIdx);
  }

  if (isForward) {
    // 顺排：找出生日之后最近的节
    for (let offset = 0; offset < 14; offset++) {
      const d = getTermDateByRawIdx(currentTermIdx + offset);
      const diff = daysBetweenExact(birthYear, birthMonth, birthDay, birthHour, birthMinute, 0, d.year, d.month, d.day, d.hours, d.minutes, d.seconds);
      if (diff > 0) { days = diff; break; }
    }
    if (days <= 0) days = 30;
  } else {
    // 逆排：找出生日之前最近的节
    for (let offset = 0; offset <= 14; offset++) {
      const d = getTermDateByRawIdx(currentTermIdx - offset);
      const diff = daysBetweenExact(d.year, d.month, d.day, d.hours, d.minutes, d.seconds, birthYear, birthMonth, birthDay, birthHour, birthMinute, 0);
      if (diff > 0) { days = diff; break; }
    }
    if (days <= 0) days = 30;
  }

  const { years, months } = calcQiyunAge(days);

  // 计算精确交运时刻：出生时刻 + (days × 3) 年对应的天数
  // 三天折一年：days 天 → years 年 months 月
  // 精确交运时刻 = 出生时刻 + days × 3 × 365.2422 天（近似）
  // 更精确：直接用 years 年 months 月 换算回天数加到出生日期
  const totalDaysToAdd = years * 365.2422 + months * 30.4375;
  const jiaoYunDt = addDaysToDate(birthYear, birthMonth, birthDay, birthHour, birthMinute, totalDaysToAdd);
  const pad = (n: number) => String(n).padStart(2, '0');
  const startDateTime = `${jiaoYunDt.year}-${pad(jiaoYunDt.month)}-${pad(jiaoYunDt.day)} ${pad(jiaoYunDt.hour)}:${pad(jiaoYunDt.minute)}`;

  return { startAge: years, startMonths: months, isForward, startDateTime };
}

function generateDayunSequence(
  monthStem: Stem, monthBranch: Branch, isForward: boolean,
): Array<{ stem: Stem; branch: Branch }> {
  const stemIdx = STEMS.indexOf(monthStem);
  const branchIdx = BRANCHES.indexOf(monthBranch);
  const result: Array<{ stem: Stem; branch: Branch }> = [];
  const direction = isForward ? 1 : -1;
  for (let i = 1; i <= 10; i++) {
    const si = ((stemIdx + i * direction) % 10 + 10) % 10;
    const bi = ((branchIdx + i * direction) % 12 + 12) % 12;
    result.push({ stem: STEMS[si], branch: BRANCHES[bi] });
  }
  return result;
}

// ═══════════════════════════════════════════════════════════════
//  核心评分引擎 — 大运与原局联动
// ═══════════════════════════════════════════════════════════════

function calcDayunScore(
  stem: Stem, branch: Branch,
  fourPillars: FourPillars,
  bodyStrength: 'strong' | 'weak' | 'neutral',
  geju: GejuResult,
): { score: number; factors: string[]; conflicts: DayunConflictItem[] } {
  let score = 60;
  const factors: string[] = [];
  const conflicts: DayunConflictItem[] = [];

  const dayStem = fourPillars.day.stem;
  const dayStemEl = STEM_ELEMENT[dayStem];
  const dyStemEl = STEM_ELEMENT[stem];
  const dyBranchEl = getBranchMainEl(branch);

  // 喜忌
  const xi = bodyStrength === 'strong'
    ? [ELEMENT_OVERCOME_BY[dayStemEl], ELEMENT_GENERATES[dayStemEl], ELEMENT_OVERCOMES[dayStemEl]]
    : bodyStrength === 'weak'
      ? [dayStemEl, ELEMENT_GENERATED_BY[dayStemEl]]
      : [ELEMENT_GENERATES[dayStemEl], ELEMENT_OVERCOMES[dayStemEl]];
  const ji = bodyStrength === 'strong'
    ? [dayStemEl, ELEMENT_GENERATED_BY[dayStemEl]]
    : bodyStrength === 'weak'
      ? [ELEMENT_OVERCOME_BY[dayStemEl], ELEMENT_GENERATES[dayStemEl], ELEMENT_OVERCOMES[dayStemEl]]
      : [dayStemEl];

  // ── 1. 大运天干十神 vs 喜忌 ───────────────────────────────
  const stemSS = getShishen(dayStem, stem);
  const strengthAdj = calcStrengthAdjust(stemSS, bodyStrength);
  score += strengthAdj;
  if (strengthAdj > 0) factors.push('大运天干十神契合日主喜忌');
  else if (strengthAdj < 0) factors.push('大运天干十神对日主形成压力');

  // ── 2. 大运五行 vs 喜忌 ───────────────────────────────────
  const dyEls = [dyStemEl, dyBranchEl];
  const isXi = dyEls.some(el => xi.includes(el));
  const isJi = dyEls.some(el => ji.includes(el));
  if (isXi) { score += 8; factors.push('大运五行逢命局喜用神'); }
  else if (isJi) { score -= 8; factors.push('大运五行逢命局忌神'); }

  // ── 3. 大运与原局四柱刑冲合害 ────────────────────────────
  const allBranches = [fourPillars.year.branch, fourPillars.month.branch, fourPillars.day.branch, fourPillars.hour.branch, branch];

  // 日柱（最重要）
  if (isChong(branch, fourPillars.day.branch)) {
    score -= 10;
    conflicts.push({ type: '冲', target: '日柱地支', effect: '凶', desc: `大运${branch}冲日柱${fourPillars.day.branch}，根基动摇，健康感情需格外维护` });
  }
  if (isHe(branch, fourPillars.day.branch)) {
    score += 6;
    conflicts.push({ type: '合', target: '日柱地支', effect: '吉', desc: `大运${branch}合日柱${fourPillars.day.branch}，根基稳固，人缘桃花顺遂` });
  }
  if (isHai(branch, fourPillars.day.branch)) {
    score -= 4;
    conflicts.push({ type: '害', target: '日柱地支', effect: '凶', desc: `大运${branch}害日柱${fourPillars.day.branch}，暗中有损，防小人背后` });
  }
  if (isTianGanHe(stem, fourPillars.day.stem)) {
    score += 4;
    conflicts.push({ type: '天干合', target: '日柱天干', effect: '吉', desc: `大运天干${stem}合日主${fourPillars.day.stem}，贵人暗助` });
  }
  if (isTianGanChong(stem, fourPillars.day.stem)) {
    score -= 5;
    conflicts.push({ type: '天干冲', target: '日柱天干', effect: '凶', desc: `大运天干${stem}冲日主${fourPillars.day.stem}，气机震荡，变动较多` });
  }

  // 年柱
  if (isChong(branch, fourPillars.year.branch)) {
    score -= 4;
    conflicts.push({ type: '冲', target: '年柱地支', effect: '凶', desc: `大运${branch}冲年柱${fourPillars.year.branch}，长辈、根基可能有变` });
  }
  if (isHe(branch, fourPillars.year.branch)) {
    score += 3;
    conflicts.push({ type: '合', target: '年柱地支', effect: '吉', desc: `大运${branch}合年柱${fourPillars.year.branch}，根基稳固，长辈缘佳` });
  }

  // 月柱
  if (isHe(branch, fourPillars.month.branch)) {
    score += 3;
    conflicts.push({ type: '合', target: '月柱地支', effect: '吉', desc: `大运${branch}合月柱${fourPillars.month.branch}，事业有人提携` });
  }
  if (isChong(branch, fourPillars.month.branch)) {
    score -= 3;
    conflicts.push({ type: '冲', target: '月柱地支', effect: '凶', desc: `大运${branch}冲月柱${fourPillars.month.branch}，工作环境可能有变动` });
  }

  // 时柱
  if (isChong(branch, fourPillars.hour.branch)) {
    score -= 3;
    conflicts.push({ type: '冲', target: '时柱地支', effect: '凶', desc: `大运${branch}冲时柱${fourPillars.hour.branch}，子女晚辈关系需注意` });
  }

  // 自刑
  if (isZiXing(branch, branch)) {
    score -= 4;
    conflicts.push({ type: '刑', target: '大运地支自刑', effect: '凶', desc: `${branch}自刑，内心纠结，决策易反复` });
  }

  // 三合局（大运+原局四支）
  const sanhe = isSanHe(allBranches);
  if (sanhe) {
    score += 7;
    conflicts.push({ type: '三合', target: `${sanhe.branches.join('')}合${sanhe.element}局`, effect: '吉', desc: `大运与原局构成${sanhe.branches.join('')}${sanhe.element}三合局，大势所趋` });
  }
  if (isSanXing(allBranches)) {
    score -= 6;
    conflicts.push({ type: '刑', target: '大运原局三刑', effect: '凶', desc: '大运与原局构成三刑，是非纠缠，官非口舌需防' });
  }

  // ── 4. 格局助破 ─────────────────────────────────────────
  const helpsGeju = dyEls.some(el => geju.lucky.includes(el));
  const breaksGeju = dyEls.some(el => geju.unlucky.includes(el));
  if (helpsGeju) { score += 6; factors.push('大运五行助格局，格局得助'); }
  if (breaksGeju) { score -= 6; factors.push('大运五行破格，格局有损'); }

  // 边界
  score = Math.min(96, Math.max(12, Math.round(score)));
  return { score, factors, conflicts };
}

function calcStrengthAdjust(shishen: Shishen, bodyStrength: 'strong' | 'weak' | 'neutral'): number {
  if (bodyStrength === 'strong') {
    if (['正官', '七杀'].includes(shishen)) return +8;
    if (['食神', '伤官'].includes(shishen)) return +8;
    if (['正财', '偏财'].includes(shishen)) return +5;
    if (['比肩', '劫财'].includes(shishen)) return -8;
    if (['正印', '偏印'].includes(shishen)) return -5;
  } else if (bodyStrength === 'weak') {
    if (['比肩', '劫财'].includes(shishen)) return +8;
    if (['正印', '偏印'].includes(shishen)) return +8;
    if (['正官', '七杀'].includes(shishen)) return -8;
    if (['食神', '伤官'].includes(shishen)) return -5;
    if (['正财', '偏财'].includes(shishen)) return -5;
  }
  return 0;
}

function scoreToLevel(score: number): DayunDomain['level'] {
  if (score >= 82) return '优秀';
  if (score >= 70) return '良好';
  if (score >= 58) return '平稳';
  if (score >= 45) return '注意';
  return '谨慎';
}

function scoreToOverallLevel(score: number): DayunItem['overallLevel'] {
  if (score >= 80) return '大吉';
  if (score >= 68) return '吉';
  if (score >= 56) return '平顺';
  if (score >= 44) return '需注意';
  return '挑战';
}

// ── 四域评分 ──────────────────────────────────────────────────
function calcDomainScores(
  stemSS: Shishen,
  branchSS: Shishen,
  bodyStrength: 'strong' | 'weak' | 'neutral',
  conflicts: DayunConflictItem[],
  xi: Element[], ji: Element[],
  stemEl: Element, branchEl: Element,
): Record<'career' | 'wealth' | 'love' | 'health', DayunDomain> {
  const base = { career: 60, wealth: 60, love: 60, health: 60 };
  const hasChong = conflicts.some(c => c.type === '冲');
  const hasXing = conflicts.some(c => c.type === '刑');
  const hasHe = conflicts.some(c => c.type === '合');
  const riChong = conflicts.some(c => c.target.includes('日柱') && c.type === '冲');
  const riHe = conflicts.some(c => c.target.includes('日柱') && c.type === '合');

  // 事业
  if (['正官', '七杀'].includes(stemSS)) { base.career += 8; }
  if (['食神', '伤官'].includes(stemSS)) { base.career += 5; }
  if (hasChong) base.career -= 6;
  if (hasHe) base.career += 4;
  if (bodyStrength === 'weak' && ['正官', '七杀'].includes(stemSS)) base.career -= 5;

  // 财运
  if (['正财', '偏财'].includes(stemSS)) { base.wealth += 8; }
  if (['食神', '伤官'].includes(stemSS)) base.wealth += 4;
  if (hasChong) base.wealth -= 5;
  if (conflicts.some(c => c.type === '破')) base.wealth -= 4;
  if (bodyStrength === 'weak' && ['正财', '偏财'].includes(stemSS)) base.wealth -= 4;

  // 感情
  if (['正官', '正财', '偏财'].includes(stemSS)) base.love += 5;
  if (riHe) base.love += 5;
  if (['七杀', '伤官', '劫财'].includes(stemSS)) base.love -= 4;
  if (riChong) base.love -= 6;

  // 健康
  if (riChong) base.health -= 6;
  if (hasXing) base.health -= 4;
  if (bodyStrength === 'weak' && ['正官', '七杀', '食神', '伤官', '正财', '偏财'].includes(stemSS)) base.health -= 3;
  if (['正印', '比肩'].includes(stemSS)) base.health += 3;

  // 喜忌
  const isXi = xi.includes(stemEl) || xi.includes(branchEl);
  const isJi = ji.includes(stemEl) || ji.includes(branchEl);
  if (isXi) { base.career += 3; base.wealth += 3; base.love += 3; base.health += 3; }
  if (isJi) { base.career -= 3; base.wealth -= 3; base.love -= 3; base.health -= 3; }

  return {
    career: {
      score: Math.min(96, Math.max(15, Math.round(base.career))),
      level: scoreToLevel(base.career),
      desc: buildCareerDesc(stemSS, bodyStrength, conflicts),
    },
    wealth: {
      score: Math.min(96, Math.max(15, Math.round(base.wealth))),
      level: scoreToLevel(base.wealth),
      desc: buildWealthDesc(stemSS, bodyStrength, conflicts),
    },
    love: {
      score: Math.min(96, Math.max(15, Math.round(base.love))),
      level: scoreToLevel(base.love),
      desc: buildLoveDesc(stemSS, bodyStrength, conflicts),
    },
    health: {
      score: Math.min(96, Math.max(15, Math.round(base.health))),
      level: scoreToLevel(base.health),
      desc: buildHealthDesc(stemSS, bodyStrength, conflicts, stemEl),
    },
  };
}

// ── 四域描述（联动逻辑，非模板）───────────────────────────────
function buildCareerDesc(stemSS: Shishen, bodyStrength: 'strong' | 'weak' | 'neutral', conflicts: DayunConflictItem[]): string {
  const parts: string[] = [];
  const hasChong = conflicts.some(c => c.type === '冲');
  const hasHe = conflicts.some(c => c.type === '合');

  if (['正官', '七杀'].includes(stemSS)) {
    parts.push('事业方面权力与责任并存');
    if (bodyStrength === 'strong') parts.push('身强可担，适合争取更大的管理舞台，晋升窗口打开');
    else parts.push('身弱压力偏大，宜稳扎稳打，循序渐进');
  } else if (['食神', '伤官'].includes(stemSS)) {
    parts.push('创意与才华是此十年职场核心竞争力');
    parts.push('适合在需要创新、表达、突破的领域深耕，才华变现路径通畅');
  } else if (['正财', '偏财'].includes(stemSS)) {
    parts.push('财富驱动事业，业绩导向明显');
    if (hasHe) parts.push('合作生财，宜借助团队与平台力量拓展事业版图');
  } else {
    parts.push('事业节奏平稳，适合深耕已有领域，积累专业深度');
  }

  if (hasChong) parts.push('大运冲克原局，事业变动较多，转型跳槽需慎重评估');
  if (hasHe && !hasChong) parts.push('人际和合，贵人提携，推进项目事半功倍');

  return parts.join('；') + '。';
}

function buildWealthDesc(stemSS: Shishen, bodyStrength: 'strong' | 'weak' | 'neutral', conflicts: DayunConflictItem[]): string {
  const parts: string[] = [];
  const hasChong = conflicts.some(c => c.type === '冲');

  if (['正财', '偏财'].includes(stemSS)) {
    parts.push('财运活跃，进财渠道多元');
    if (bodyStrength === 'strong') parts.push('身强可担财，适合稳健投资和资产积累');
    else parts.push('身弱财重，财来财去，建议以守为主，量入为出');
  } else if (['食神', '伤官'].includes(stemSS)) {
    parts.push('才华变现路径通畅，技能与创意可直接转化为收入');
  } else if (['比肩', '劫财'].includes(stemSS)) {
    parts.push('竞争环境激烈，需防财务纠纷和人际损耗');
    if (hasChong) parts.push('冲克之年，切忌借贷担保，守住已有积累');
  } else {
    parts.push('财运平稳，按部就班积累即可');
  }

  if (conflicts.some(c => c.type === '破')) parts.push('相破之运，注意财物损耗，守住已有积累');

  return parts.join('；') + '。';
}

function buildLoveDesc(stemSS: Shishen, bodyStrength: 'strong' | 'weak' | 'neutral', conflicts: DayunConflictItem[]): string {
  const parts: string[] = [];
  const riChong = conflicts.some(c => c.target.includes('日柱') && c.type === '冲');
  const riHe = conflicts.some(c => c.target.includes('日柱') && c.type === '合');

  if (['正官', '正财', '偏财'].includes(stemSS)) {
    parts.push('感情趋向稳定，关系中有承诺和责任的能量');
    if (riHe) parts.push('大运合日柱，正缘机遇或感情升温，婚姻窗口打开');
  } else if (['七杀', '伤官', '劫财'].includes(stemSS)) {
    parts.push('感情波动较大，易有争执或外缘干扰');
    if (riChong) parts.push('冲日柱之运，感情根基受考验，需主动维护沟通');
  } else {
    parts.push('感情节奏平稳，宜在日常相处中增进默契');
  }

  if (riHe && !riChong) parts.push('人缘桃花顺遂，单身者可积极社交');

  return parts.join('；') + '。';
}

function buildHealthDesc(stemSS: Shishen, bodyStrength: 'strong' | 'weak' | 'neutral', conflicts: DayunConflictItem[], stemEl: Element): string {
  const parts: string[] = [];
  const riChong = conflicts.some(c => c.target.includes('日柱') && c.type === '冲');
  const hasXing = conflicts.some(c => c.type === '刑');

  const organMap: Record<Element, string> = {
    木: '肝胆', 火: '心与小肠', 土: '脾胃', 金: '肺与大肠', 水: '肾与膀胱',
  };
  parts.push(`大运${stemEl}气主导，十年间注意${organMap[stemEl]}方面的系统养护`);

  if (riChong) parts.push('大运冲日柱，身体根基受扰，注意休息与情绪管理');
  if (hasXing) parts.push('刑克之运，气血运行可能不畅，建议规律运动和定期体检');
  if (bodyStrength === 'weak' && ['正官', '七杀', '食神', '伤官', '正财', '偏财'].includes(stemSS)) {
    parts.push('身弱逢克泄之运，精力消耗大，防止过劳，劳逸结合');
  }

  return parts.join('；') + '。';
}

// ── 十年主题 / 触发点 / 风险位 / 发力方向 ──────────────────────
function buildDecadeTheme(
  stemSS: Shishen, branch: Branch,
  bodyStrength: 'strong' | 'weak' | 'neutral',
  conflicts: DayunConflictItem[],
): { theme: string; trigger: string; risk: string; proactive: string } {
  const hasChong = conflicts.some(c => c.type === '冲');
  const hasXing = conflicts.some(c => c.type === '刑');
  const hasHe = conflicts.some(c => c.type === '合' || c.type === '天干合');
  const hasSanHe = conflicts.some(c => c.type === '三合');

  // 十年主题
  let theme = '';
  if (hasChong || hasXing) {
    theme = `此十年是变革与调整之运，外界环境推动您走出舒适区，在变局中寻新方向。${branch}支与原局形成冲刑，变动之中藏机遇，主动求变方可破局。这不是最舒适的十年，但往往是成长最多的十年——只要提前做好风险管理，压力完全可以转成成长机会。`;
  } else if (hasHe || hasSanHe) {
    theme = `此十年是和合与机遇之运，天时地利人和齐聚，适合推进人生重要计划。${stemSS}星主导，顺势而为可获长足发展。这是难得的顺风期，务必在此期间积极推进核心目标。`;
  } else if (['正官', '正印'].includes(stemSS)) {
    theme = '此十年是积累与进阶之运，稳健推进，厚积薄发。适合深耕专业、建立声誉，是事业或学业奠基的关键期。规律、踏实、诚信，是这十年最好的策略。';
  } else if (['七杀', '伤官'].includes(stemSS)) {
    theme = '此十年是挑战与突破之运，逆境激发潜能，主动求变方可破局。压力下往往能展现超出预期的实力。记住：七杀有制则为权，这十年正是锻造您核心竞争力的黄金期。';
  } else if (['食神', '偏财'].includes(stemSS)) {
    theme = '此十年是才华与机遇之运，创造力旺盛，适合展示自我、拓展人脉、多元发展。天赋在这十年最容易被看见，主动出击、积极呈现，机遇会主动找上门。';
  } else if (['正财', '比肩'].includes(stemSS)) {
    theme = '此十年是务实与协作之运，踏实积累，合作共赢。稳中求进，不宜冒进，每一步扎实的积累都在为未来蓄力。';
  } else {
    theme = '此十年是平稳发展之运，守成固本，静待时机。在稳定中打磨内功，蓄势待发，等待更有利的流年窗口。';
  }

  // 触发点
  let trigger = '';
  const goodConflicts = conflicts.filter(c => c.effect === '吉');
  if (goodConflicts.some(c => c.type === '合')) {
    trigger = '人际合作、贵人引荐、感情升温是此十年关键触发点。大运合原局之年份尤为关键，留意身边合作机会与贵人引荐。每一次真诚的连接都可能是改变命运走向的节点。';
  } else if (goodConflicts.some(c => c.type === '三合')) {
    trigger = '大局成形、资源整合是核心触发点，适合启动需多方配合的大项目或人生大事（创业、置业、婚姻）。三合局成形之年，是您行动最应该果断的窗口。';
  } else if (['正官', '正印'].includes(stemSS)) {
    trigger = '上级赏识、资质认证、规则认可将是您此十年的关键触发点。主动争取评优、考试、晋升机会——这十年规则体系是您最好的朋友，善用它。';
  } else if (['食神', '伤官'].includes(stemSS)) {
    trigger = '才华展示、创意落地、作品发布将带来突破性机遇。适合推出个人品牌、出版作品、开展副业，让世界看见您真实的价值。';
  } else if (['正财', '偏财'].includes(stemSS)) {
    trigger = '财务规划、投资机会、副业拓展是此十年的财富触发点。每隔两到三年会有进财窗口，提前布局、保持弹性，是把握这些窗口的核心策略。';
  } else {
    trigger = '每运的前两年和后两年是两个关键时间节点，适合布局和调整。中年阶段的转折尤为关键，定期复盘十年目标有助于及时校准方向。';
  }

  // 风险位（调适建议结构）
  let risk = '';
  const badConflicts = conflicts.filter(c => c.effect === '凶');
  if (badConflicts.some(c => c.type === '冲')) {
    risk = `冲克之运整体有变动，健康、感情、合作关系是重点风险位。这不是"坏运"，而是"提醒您这一阶段更适合稳住节奏"的信号。
【命理原因】大运地支与原局形成六冲，气机震荡，根基受扰。
【现实表现】容易遭遇突发变动（职位调整、感情波折、健康隐患），情绪起伏较大。
【调和方法】
① 重大决策避开冲克叠加的流年；
② 合同、健康、关系都留检查点，分阶段推进；
③ 减少冲动决策，重要沟通留缓冲期；
④ 规律作息，避免情绪积压；
⑤ 如有身体不适，以专业医疗建议为准，命理分析仅为辅助参考。
【鼓励收束】只要提前做好风险管理，冲克之运反而能让您在压力中发现以往忽视的机会。`;
  } else if (badConflicts.some(c => c.type === '刑')) {
    risk = `是非纠缠之运，法律合同、口舌纷争、情绪管理是风险位。
【命理原因】三刑或相刑主是非，气机不畅，内外摩擦多。
【现实表现】容易遇到口舌纠纷、合作矛盾或官司合同问题，关系中硬碰硬容易两败俱伤。
【调和方法】
① 遇事多忍让少争执，凡事留痕；
② 合同文书仔细审核，口头承诺不可轻信；
③ 避免卷入他人纠纷；
④ 在关系中避免硬碰硬，留缓冲期再沟通。
【鼓励收束】是非之运也是磨砺智慧的时期，学会以柔化刚、调和矛盾，反而能建立更成熟稳固的人际格局。`;
  } else if (badConflicts.some(c => c.type === '害')) {
    risk = `暗藏隐患之运，人际关系需防小人，财务往来要留凭证，不宜轻信他人承诺。
【调和方法】
① 多亲近贵人，减少不必要的社交应酬；
② 财务往来留书面凭证；
③ 信任需要时间验证，不宜过快投入。
【鼓励收束】保持觉察，不是变得多疑，而是更有边界感。此运结束后，您的识人辨事能力会有显著提升。`;
  } else if (['七杀', '劫财'].includes(stemSS)) {
    risk = `压力与竞争并存，健康与财务是重点保护位。
【调和方法】
① 不宜冒进投资和过激行为；
② 规律运动以疏导七杀之气；
③ 建立流程与边界，降低冒进风险；
④ 健康如有不适，以专业医疗建议为准。
【鼓励收束】七杀有制则为权——这十年正是锻造您核心竞争力的黄金期，主动设定目标和期限，把压力化为动力。`;
  } else if (bodyStrength === 'weak' && ['正官', '七杀', '食神', '伤官', '正财', '偏财'].includes(stemSS)) {
    risk = `身弱逢克泄之运，精力与健康是首要风险位。
【命理原因】日主根气不足，大运五行消耗较大，精力恢复偏慢。
【现实表现】容易感到体力不支、精神疲倦，任务过多时压力倍增。
【调和方法】
① 劳逸结合，量力而行；
② 减少高压、高消耗的项目；
③ 增加休息与恢复时段；
④ 多接触喜用神五行以补充能量；
⑤ 如有不适，以专业医疗建议为准。
【鼓励收束】稳扎稳打同样可以积累出色的成果，身弱之人往往更善合作、更懂借力，这是您特有的优势。`;
  } else {
    risk = `整体平稳，但需防过度乐观导致的松懈。保持警觉，定期复盘十年目标执行情况。平顺之运也要居安思危，为下一个挑战期提前蓄力。`;
  }

  // 主动发力方向
  let proactive = '';
  if (bodyStrength === 'strong') {
    if (['正官', '七杀'].includes(stemSS)) {
      proactive = '适合主动争取晋升、扩大管理权限、承担更大责任。将压力转化为动力，在竞争中脱颖而出。此十年是您展现领导力的黄金窗口，不宜退缩。';
    } else if (['食神', '伤官'].includes(stemSS)) {
      proactive = '适合主动展示才华、推出新作品、开拓个人品牌。将创造力转化为实际成就，让世界看见您的价值。才华变现的窗口正开，把握每一个展示机会。';
    } else if (['正财', '偏财'].includes(stemSS)) {
      proactive = '适合主动理财规划、拓展收入渠道、参与商业合作。财富增值的关键十年，稳健投资与积极拓展并重。';
    } else {
      proactive = '适合主动学习充电、考证进修、扩大人脉，为下一个十年蓄力。这十年的积累，将成为未来十年爆发的基础。';
    }
  } else if (bodyStrength === 'weak') {
    if (['正印', '偏印', '比肩', '劫财'].includes(stemSS)) {
      proactive = '适合主动寻求师长提携、结交盟友、借助外力壮大自身。合作共赢是最佳策略，借力之智不亚于独力之强。';
    } else {
      proactive = '适合主动固本培元、精简项目、聚焦核心竞争力。少做多精，以守为攻。先把根扎深，果实自然会来。';
    }
  } else {
    proactive = '适合主动平衡各方面关系，稳健推进既有计划，不急不躁，把握节奏。旺年可进取、衰年宜守成，动态调整是中和之格的最大优势。';
  }

  return { theme, trigger, risk, proactive };
}

// ── 建议生成 ──────────────────────────────────────────────────
function buildKeyAdvice(
  stemSS: Shishen, bodyStrength: 'strong' | 'weak' | 'neutral',
  overallLevel: DayunItem['overallLevel'],
  conflicts: DayunConflictItem[],
): string[] {
  const advice: string[] = [];
  const hasChong = conflicts.some(c => c.type === '冲');
  const hasXing = conflicts.some(c => c.type === '刑');

  if (overallLevel === '大吉' || overallLevel === '吉') {
    advice.push('大运吉顺，把握十年黄金窗口，主动推进核心人生计划');
    if (['食神', '伤官'].includes(stemSS)) advice.push('才华变现的黄金期，大胆展示个人能力与创意成果');
    if (['正财', '偏财'].includes(stemSS)) advice.push('财务机遇多，适合稳健投资与资产配置');
  } else if (overallLevel === '需注意' || overallLevel === '挑战') {
    advice.push('大运有波动，重大决策前多方评估，预留弹性空间');
    if (hasChong) advice.push('冲克之运，健康与感情需格外维护，避免冲动行事');
    if (hasXing) advice.push('是非之运，合同文书仔细审核，减少口头承诺');
    if (bodyStrength === 'weak') advice.push('身弱逢大运压力，优先固本培元，不宜透支精力');
  } else {
    advice.push('运势平稳，脚踏实地积累，等待更有利的流年窗口');
  }

  if (advice.length < 3) advice.push('定期复盘十年目标，及时调整策略方向');
  return advice.slice(0, 3);
}

// ═══════════════════════════════════════════════════════════════
//  公开接口 — 主计算函数
// ═══════════════════════════════════════════════════════════════

export function calcDayun(
  fourPillars: FourPillars,
  birthYear: number, birthMonth: number, birthDay: number,
  gender: 'male' | 'female', currentYear: number,
  birthHour = 0, birthMinute = 0,
): DayunResult {
  const { year: yearPillar, month: monthPillar, day: dayPillar } = fourPillars;
  const dayStem = dayPillar.stem;

  const { startAge, startMonths, isForward, startDateTime } = calcStartAgeAndDirection(
    birthYear, birthMonth, birthDay, birthHour, birthMinute, yearPillar.stem, gender,
  );

  const bodyStrength = calcDayStrength(fourPillars);
  const geju = judgeGeju(fourPillars);

  const sequence = generateDayunSequence(monthPillar.stem, monthPillar.branch, isForward);
  const currentAge = currentYear - birthYear;

  const items: DayunItem[] = sequence.map((d, i) => {
    const age = startAge + i * 10;
    if (age > 100) return null;
    const rawEndAge = age + 9;
    const endAge = Math.min(100, rawEndAge);
    const startYear = birthYear + age - 1;
    const endYear = birthYear + endAge - 1;
    const stemShishen = getShishen(dayStem, d.stem);
    const branchMainStem = getBranchMainStem(d.branch);
    const branchMainShishen = branchMainStem ? getShishen(dayStem, branchMainStem) : stemShishen;
    const isActive = currentAge >= age && currentAge <= endAge;
    const stemEl = STEM_ELEMENT[d.stem];
    const branchEl = getBranchMainEl(d.branch);

    // 核心联动评分
    const { score, factors, conflicts } = calcDayunScore(d.stem, d.branch, fourPillars, bodyStrength, geju);
    const overallScore = score;
    const overallLevel = scoreToOverallLevel(overallScore);

    const xi = bodyStrength === 'strong'
      ? [ELEMENT_OVERCOME_BY[STEM_ELEMENT[dayStem]], ELEMENT_GENERATES[STEM_ELEMENT[dayStem]], ELEMENT_OVERCOMES[STEM_ELEMENT[dayStem]]]
      : bodyStrength === 'weak'
        ? [STEM_ELEMENT[dayStem], ELEMENT_GENERATED_BY[STEM_ELEMENT[dayStem]]]
        : [ELEMENT_GENERATES[STEM_ELEMENT[dayStem]], ELEMENT_OVERCOMES[STEM_ELEMENT[dayStem]]];
    const ji = bodyStrength === 'strong'
      ? [STEM_ELEMENT[dayStem], ELEMENT_GENERATED_BY[STEM_ELEMENT[dayStem]]]
      : bodyStrength === 'weak'
        ? [ELEMENT_OVERCOME_BY[STEM_ELEMENT[dayStem]], ELEMENT_GENERATES[STEM_ELEMENT[dayStem]], ELEMENT_OVERCOMES[STEM_ELEMENT[dayStem]]]
        : [STEM_ELEMENT[dayStem]];

    const domains = calcDomainScores(stemShishen, branchMainShishen, bodyStrength, conflicts, xi, ji, stemEl, branchEl);
    const { theme, trigger, risk, proactive } = buildDecadeTheme(stemShishen, d.branch, bodyStrength, conflicts);
    const keyAdvice = buildKeyAdvice(stemShishen, bodyStrength, overallLevel, conflicts);

    return {
      index: i + 1,
      stem: d.stem,
      branch: d.branch,
      startAge: age,
      startMonths: i === 0 ? startMonths : 0,
      endAge,
      startYear,
      endYear,
      stemShishen,
      branchMainShishen,
      stemColor: ELEMENT_COLORS[stemEl],
      branchColor: branchMainStem ? ELEMENT_COLORS[STEM_ELEMENT[branchMainStem]] : ELEMENT_COLORS[stemEl],
      isActive,
      stemEl,
      branchEl,
      bodyStrength,
      xiElements: xi,
      jiElements: ji,
      conflicts,
      overallScore,
      overallLevel,
      decadeTheme: theme,
      triggerPoint: trigger,
      riskArea: risk,
      proactive,
      career: domains.career,
      wealth: domains.wealth,
      love: domains.love,
      health: domains.health,
      keyAdvice,
      factors,
    };
  }).filter((item): item is DayunItem => item !== null);

  const currentDayun = items.find(it => it.isActive);

  return { startAge, startMonths, isForward, startDateTime, items, currentDayun };
}

// ── 兼容旧接口 ────────────────────────────────────────────────
function getBranchMainStem_internal(branch: Branch): Stem | null {
  const map: Record<Branch, Stem> = {
    子: '癸', 丑: '己', 寅: '甲', 卯: '乙', 辰: '戊', 巳: '丙',
    午: '丁', 未: '己', 申: '庚', 酉: '辛', 戌: '戊', 亥: '壬',
  };
  return map[branch] ?? null;
}

export function calcLiunian(year: number): { stem: Stem; branch: Branch } {
  const stemIdx = ((year - 4) % 10 + 10) % 10;
  const branchIdx = ((year - 4) % 12 + 12) % 12;
  return { stem: STEMS[stemIdx], branch: BRANCHES[branchIdx] };
}

export function getDayunLevelColor(level: DayunItem['overallLevel']): string {
  const map: Record<DayunItem['overallLevel'], string> = {
    '大吉': '#E8A020',
    '吉': '#7BC67E',
    '平顺': '#4A90D9',
    '需注意': '#B79AC5',
    '挑战': '#D14B45',
  };
  return map[level];
}